import React from "react"
import styled from "styled-components"
import { fontSize, fontWeight, colors } from "buoy-theme"
import { ButtonLink } from "buoy-core"
import Arrow from "../images/Arrow"
import { CardSection } from "./Card"
import { CardLabel } from "../../common/components/Label"
import { capitalizeString } from "../../common/common"
import DiagnosisAgreement from "../../feedback/components/DiagnosisAgreement"
import MarkdownSection, { ListItem, List, Paragraph } from "./MarkdownSection"
import { sectionActionTracker } from "../actions"

const SectionContainer = styled.div`
  width: 100%;
  margin-top: 16px;
  border-top: solid 1px ${colors.neutralLight};
`

const ExpandArrow = styled(Arrow)`
  display: inline-flex;
  margin: 0 8px;
  vertical-align: middle;
`

const Header = styled.h2`
  ${fontSize("h3")};
  font-weight: ${fontWeight.regular};
  margin: 0 auto 4px;
`

const ContentHeader = styled.h3`
  ${fontSize("bodyMain")};
  font-weight: ${fontWeight.regular};
  margin-bottom: 8px;
`

const Note = styled.p`
  ${fontSize("bodySmall")};
  font-weight: ${fontWeight.regular};
  padding: 0;
  margin: 0 auto 8px;
`

const ExpandLink = styled(ButtonLink).attrs({
  fontSize: "bodyMain",
})`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
  width: 100%;
  ${fontSize("bodyMain")};
  color: ${props => (props.email ? colors.primary : colors.neutralDarkest)};
  text-decoration: none;
  font-weight: ${fontWeight.regular};
  margin-bottom: 12px;
`

const TextSectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-content: flex-start;
`

const textSections = (adviceContent, forSub, list) => {
  const content = []

  for (const property in adviceContent) {
    content.push(
      <React.Fragment key={property}>
        {property !== "other" && (
          <Paragraph marginBottom="8px" marginTop="12px">
            {forSub ? `For ${property}:` : capitalizeString(property)}
          </Paragraph>
        )}
        {property !== "other" || list ? (
          <List>
            {adviceContent[property].map((description, i) => (
              <ListItem key={i}>
                <MarkdownSection source={description} />
              </ListItem>
            ))}
          </List>
        ) : (
          <React.Fragment>
            {adviceContent[property].map((description, i) => (
              <MarkdownSection key={i} source={description} />
            ))}
          </React.Fragment>
        )}
      </React.Fragment>
    )
  }
  return content
}

const renderTextSections = adviceContent => {
  const content = []

  for (const property in adviceContent) {
    if (property === "other") {
      return (
        <TextSectionContainer key={property}>
          {adviceContent.other.map((description, i) => (
            <MarkdownSection key={i} source={description} />
          ))}
        </TextSectionContainer>
      )
    } else if (
      property === "Medication" ||
      property === "Stretches and exercises"
    ) {
      content.push(
        <TextSectionContainer key={property}>
          {property !== "other" && <ContentHeader>{property}</ContentHeader>}
          {property === "Medication" && (
            <MarkdownSection source="To confirm you can take these medications safely, click the
          medication names below. Follow package instructions." />
          )}
          {textSections(adviceContent[property], true)}
        </TextSectionContainer>
      )
    } else if (property === "What to avoid") {
      content.push(
        <TextSectionContainer key={property}>
          {property !== "other" && <ContentHeader>{property}</ContentHeader>}
          <MarkdownSection source="Based on the medical literature and expert opinion, here are some important things to avoid:" />
          {textSections(adviceContent[property], false, true)}
        </TextSectionContainer>
      )
    } else {
      content.push(
        <TextSectionContainer key={property}>
          {property !== "other" && <ContentHeader>{property}</ContentHeader>}
          {textSections(adviceContent[property])}
        </TextSectionContainer>
      )
    }
  }

  const sortingArr = [
    "the basics",
    "first aid",
    "medication",
    "hydration",
    "nutrition",
    "stretches and exercises",
    "heat or ice?",
    "rest",
    "what to avoid",
    "lifestyle",
  ]

  // sorts the content depending on the key and anything not on the sorting arr gets sent to the bottom
  content.sort((a, b) => {
    // if the item is not on the sortingArr list the item gets sent to the bottom of the list
    if (sortingArr.indexOf(a.key.toLowerCase()) === -1) {
      return 1
    } else if (sortingArr.indexOf(b.key.toLowerCase()) === -1) {
      return -1
    } else {
      return (
        sortingArr.indexOf(a.key.toLowerCase()) -
        sortingArr.indexOf(b.key.toLowerCase())
      )
    }
  })

  return content
}

const renderExpandableSections = (advice, index) => {
  const {
    "When to see a healthcare provider": docAdvice,
    ...adviceArray
  } = advice
  if (docAdvice) {
    return (
      <React.Fragment>
        <ExpandableSectionContainer
          title="How to feel better"
          key="How to feel better"
          type="care"
          index={index}
          advice={adviceArray}
        />
        <ExpandableSectionContainer
          title="When to see a healthcare provider"
          key="When to see a healthcare provider"
          type="timing"
          index={index}
          advice={docAdvice}
        />
      </React.Fragment>
    )
  }

  return (
    <ExpandableSectionContainer
      title="How to feel better"
      key={adviceArray}
      advice={adviceArray}
    />
  )
}

const ExpandableSection = ({
  expanded,
  toggleExpanded,
  title,
  expandable,
  advice,
  ...props
}) => {
  return (
    <React.Fragment>
      {expandable && (
        <SectionContainer {...props}>
          <ExpandLink onClick={toggleExpanded}>
            {title}
            <ExpandArrow
              color="primary"
              rotation={expanded ? "180deg" : 0}
              width="14"
              height="7"
            />
          </ExpandLink>
        </SectionContainer>
      )}
      {expanded && advice && renderTextSections(advice)}
    </React.Fragment>
  )
}

const EmailExpandableSection = ({
  expanded,
  toggleExpanded,
  expandable,
  ...props
}) => {
  return (
    <React.Fragment>
      {expandable && (
        <SectionContainer {...props}>
          <ExpandLink onClick={toggleExpanded} email>
            Email me this information
            <ExpandArrow
              color="primary"
              rotation={expanded ? "180deg" : 0}
              width="14"
              height="7"
            />
          </ExpandLink>
        </SectionContainer>
      )}
    </React.Fragment>
  )
}

class ExpandableSectionContainer extends React.Component {
  state = {
    expanded: false,
    email: "",
  }

  toggleExpanded = () => {
    if (this.props.type) {
      const section_title = `diagnosis match ${this.props.index+1}`
      let action
      
      if (this.state.expanded) {
        action = `collapse-${this.props.type}`
      } else {
        action = `expand-${this.props.type}`
      }
      sectionActionTracker(section_title, action)
    }

    this.setState({
      expanded: !this.state.expanded,
    })
  }

  render() {
    let { expandable, email } = this.props

    if (email) {
      return (
        <EmailExpandableSection
          {...this.props}
          {...this.state}
          toggleExpanded={expandable ? this.toggleExpanded : null}
        />
      )
    }

    return (
      <ExpandableSection
        {...this.props}
        {...this.state}
        toggleExpanded={expandable ? this.toggleExpanded : null}
      />
    )
  }
}

const SelfTreatment = ({
  label,
  header,
  diagnosis,
  note,
  index,
  selfCare,
  setDiagnosisHelpfulness,
}) => {
  return (
    <CardSection>
      {label && <CardLabel margin="0 0 10px">{label}</CardLabel>}
      {header && <Header>{header}</Header>}
      {note && <Note>{note}</Note>}
      {renderExpandableSections(selfCare, index)}
      <SectionContainer />
      <DiagnosisAgreement
        name="Was this information helpful?"
        feedback={diagnosis.helpful}
        key="Was this information helpful?"
        onSetFeedback={value => setDiagnosisHelpfulness(index, value)}
        selfCare
      />
    </CardSection>
  )
}

ExpandableSectionContainer.defaultProps = {
  showPreview: true,
  expandable: true,
  expandLink: "Read more",
  collapseLink: "Read less",
  excerpt: null,
}

export {
  SelfTreatment as default,
  ExpandableSectionContainer as ExpandableSection,
}



// WEBPACK FOOTER //
// ./assets/completion/components/SelfTreatment.js